import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, riskTone } from "@/lib/util";
import { Search, Plus, Building2, X, Loader2, ArrowUpRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { AddClientModal } from "@/components/security/AddClientModal";
import { useT } from "@/lib/i18n";
import { usePermissions } from "@/lib/permissions";
import { LicenseBadge } from "@/components/security/client/LicenseBadge";

export default function Clients() {
  const { t } = useT();
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("risk");
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      params.set("sort", sort);
      const r = await api.get(`/clients?${params}`);
      setItems(r.data);
    } finally { setLoading(false); }
  }, [q, sort]);

  useEffect(() => { load(); }, [sort, load]);
  useEffect(() => {
    const t2 = setTimeout(load, 300);
    return () => clearTimeout(t2);
  }, [q, load]);

  const totals = items.reduce((acc, c) => ({
    critical: acc.critical + (c.critical || 0),
    high: acc.high + (c.high || 0),
    assets: acc.assets + (c.assets_count || 0),
    high_risk: acc.high_risk + (c.risk_score >= 60 ? 1 : 0),
  }), { critical: 0, high: 0, assets: 0, high_risk: 0 });

  const sortOptions = [
    { id: "risk", label: t("clients.sort.risk") },
    { id: "name", label: t("clients.sort.name") },
    { id: "created", label: t("clients.sort.created") },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">{t("clients.overline")}</div>
          <h1 className="font-display font-bold tracking-tighter" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>
            {t("clients.title")}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">{t("clients.subtitle")}</p>
        </div>
        {can("client.create") && (
          <button
            onClick={() => setOpenAdd(true)}
            className="inline-flex items-center gap-2 border border-primary bg-primary text-primary-foreground px-4 py-2.5 hover:bg-primary/90 font-medium"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4" /> {t("clients.add")}
          </button>
        )}
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-border bg-card/60 p-4">
          <div className="overline">{t("clients.kpi.clients")}</div>
          <div className="font-display text-3xl mt-1">{items.length}</div>
        </div>
        <div className="border border-border bg-card/60 p-4">
          <div className="overline">{t("clients.kpi.high_risk")}</div>
          <div className="font-display text-3xl mt-1 text-sev-critical">{totals.high_risk}</div>
        </div>
        <div className="border border-border bg-card/60 p-4">
          <div className="overline">{t("clients.kpi.critical_findings")}</div>
          <div className="font-display text-3xl mt-1 text-sev-critical">{totals.critical}</div>
        </div>
        <div className="border border-border bg-card/60 p-4">
          <div className="overline">{t("clients.kpi.assets")}</div>
          <div className="font-display text-3xl mt-1">{totals.assets}</div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="flex items-center gap-2 border border-border bg-background px-3 py-2 w-full md:max-w-md">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("clients.search_placeholder")}
            className="bg-transparent border-none outline-none flex-1 font-mono text-sm"
            data-testid="clients-search"
          />
          {q && (
            <button onClick={() => setQ("")} data-testid="clients-search-clear">
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 border border-border p-1">
          <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground px-2">{t("clients.sort.label")}</span>
          {sortOptions.map(s => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              className={cn(
                "font-mono text-[10px] tracking-widest uppercase px-2 py-1",
                sort === s.id ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`sort-${s.id}`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-16 text-center text-muted-foreground font-mono text-sm">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
          {t("common.loading_portfolio")}
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border bg-card/40 p-16 text-center">
          <Building2 className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {q ? t("clients.empty_search", { q }) : t("clients.empty_none")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(c => {
            const tone = riskTone(c.risk_score);
            return (
              <Link
                key={c.id}
                to={`/clients/${c.id}`}
                className={cn(
                  "group relative border bg-card/60 hover:bg-card p-5 transition-all",
                  tone.border
                )}
                data-testid={`client-card-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-medium truncate">{c.name}</div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate">
                      {c.industry || "—"} · {c.country || "—"}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="mt-3"><LicenseBadge client={c} /></div>

                <div className="mt-5 flex items-end justify-between">
                  <div>
                    <div className="overline">{t("clients.card.risk_score")}</div>
                    <div className={cn("font-display font-bold leading-none mt-1", tone.text)}
                         style={{ fontSize: "56px" }}
                         data-testid={`client-risk-${c.id}`}>
                      {c.risk_score}
                    </div>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mt-1">
                      {t("common.of_100")} · {tone.label}
                    </div>
                  </div>
                  <div className="text-right space-y-1 font-mono text-xs">
                    {c.critical > 0 && <div className="text-sev-critical flex items-center justify-end gap-1"><ShieldAlert className="w-3 h-3" />{t("clients.card.critical", { n: c.critical })}</div>}
                    {c.high > 0 && <div className="text-sev-high">{t("clients.card.high", { n: c.high })}</div>}
                    {c.kev > 0 && <div className="text-sev-critical">{t("clients.card.kev", { n: c.kev })}</div>}
                    <div className="text-muted-foreground">{t("clients.card.assets", { n: c.assets_count })}</div>
                  </div>
                </div>

                <div className="mt-4 h-1 bg-muted overflow-hidden">
                  <div className="h-full transition-all"
                       style={{ width: `${c.risk_score}%`, backgroundColor: riskTone(c.risk_score).hex }} />
                </div>

                {c.root_domains?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {c.root_domains.slice(0, 3).map(d => (
                      <span key={d} className="font-mono text-[10px] border border-border px-1.5 py-0.5 text-muted-foreground">
                        {d}
                      </span>
                    ))}
                    {c.root_domains.length > 3 && (
                      <span className="font-mono text-[10px] text-muted-foreground">
                        +{c.root_domains.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {openAdd && <AddClientModal onClose={() => setOpenAdd(false)} onCreated={() => { setOpenAdd(false); load(); toast.success(t("clients.added_toast")); }} />}
    </div>
  );
}
